import { EuiFlexGroup, EuiFlexItem } from "@inscopix/ideas-eui";
import { TaskStatus, RunStatus } from "types/constants";
import { useDataTableContext } from "../store/DataTableProvider";
import { isNonNull } from "utils/isNonNull";

import { EuiBadge, EuiToolTip } from "@inscopix/ideas-eui";
import { ColorsByTaskStatus } from "types/ColorsByTaskStatus";
import { isExecutableTable } from "../store/DataTableProvider.helpers";

interface TaskCountBadgeProps {
  isLoading: boolean;
  marginLeft?: number;
  numTasksByStatus: {
    [TaskStatus.COMPLETE]: number;
    [TaskStatus.RUNNING]: number;
    [TaskStatus.QUEUED]: number;
    [TaskStatus.FAILED]: number;
  };
  taskStatus:
    | TaskStatus.COMPLETE
    | TaskStatus.RUNNING
    | TaskStatus.QUEUED
    | TaskStatus.FAILED;
}

const TaskCountBadge = ({
  isLoading,
  marginLeft,
  numTasksByStatus,
  taskStatus,
}: TaskCountBadgeProps) => {
  const numTasks = numTasksByStatus[taskStatus];

  const taskStatusAsString = (() => {
    switch (taskStatus) {
      case TaskStatus.COMPLETE:
        return "completed";
      case TaskStatus.RUNNING:
        return "running";
      case TaskStatus.QUEUED:
        return "queued";
      case TaskStatus.FAILED:
        return "failed";
    }
  })();

  const tooltipContent = (() => {
    if (isLoading) {
      return ""; // Hide tooltip
    } else if (numTasks === 1) {
      return `${numTasks} analysis ${taskStatusAsString}`; // e.g. 1 analysis running
    } else {
      return `${numTasks} analyses ${taskStatusAsString}`; // e.g. 4 analyses completed
    }
  })();

  return (
    <EuiToolTip content={tooltipContent} position="top">
      <EuiBadge
        color={ColorsByTaskStatus[taskStatus]}
        style={{ marginLeft }}
        title=""
      >
        {isLoading ? "-" : numTasks}
      </EuiBadge>
    </EuiToolTip>
  );
};

interface DataTableTaskCountsProps {
  tableId: string;
}

export const DataTableTaskCounts = ({ tableId }: DataTableTaskCountsProps) => {
  const tasks = useDataTableContext((s) => {
    const table = s.tables
      .filter(isExecutableTable)
      .find((table) => table.id === tableId);
    return (table?.rows ?? [])
      .map((row) =>
        "task" in row && row.task !== null
          ? { kind: "analysis" as const, task: row.task }
          : "run" in row && row.run !== null
            ? { kind: "workflow" as const, run: row.run }
            : null,
      )
      .filter(isNonNull);
  });

  const numTasksByStatus = tasks.reduce(
    (statusMap, row) => {
      switch (row.kind) {
        case "analysis": {
          const status = row.task.status as TaskStatus;
          switch (status) {
            case TaskStatus.COMPLETE:
              statusMap[TaskStatus.COMPLETE] += 1;
              break;

            case TaskStatus.NEW:
            case TaskStatus.QUEUED:
            case TaskStatus.INITIALIZING:
            case TaskStatus.PENDING:
              statusMap[TaskStatus.QUEUED] += 1;
              break;

            case TaskStatus.RUNNING:
            case TaskStatus.PROCESSING:
              statusMap[TaskStatus.RUNNING] += 1;
              break;

            case TaskStatus.FAILED:
            case TaskStatus.ERROR:
              statusMap[TaskStatus.FAILED] += 1;
              break;

            case TaskStatus.PAUSED:
            case TaskStatus.CANCELED:
            // not counted
          }
          break;
        }
        case "workflow": {
          const status = row.run.status as RunStatus;
          switch (status) {
            case RunStatus.NEW:
              statusMap[TaskStatus.QUEUED] += 1;
              break;
            case RunStatus.RUNNING:
              statusMap[TaskStatus.RUNNING] += 1;
              break;
            case RunStatus.FAILED:
              statusMap[TaskStatus.FAILED] += 1;
              break;
            case RunStatus.COMPLETE:
              statusMap[TaskStatus.COMPLETE] += 1;
              break;
          }
        }
      }
      return statusMap;
    },
    {
      [TaskStatus.COMPLETE]: 0,
      [TaskStatus.RUNNING]: 0,
      [TaskStatus.QUEUED]: 0,
      [TaskStatus.FAILED]: 0,
    },
  );

  return (
    <EuiFlexGroup gutterSize="xs">
      {(
        [
          TaskStatus.COMPLETE,
          TaskStatus.RUNNING,
          TaskStatus.QUEUED,
          TaskStatus.FAILED,
        ] as const
      )
        .filter((status) => numTasksByStatus[status] > 0)
        .map((status) => (
          <EuiFlexItem key={status}>
            <TaskCountBadge
              isLoading={false}
              numTasksByStatus={numTasksByStatus}
              taskStatus={status}
            />
          </EuiFlexItem>
        ))}
    </EuiFlexGroup>
  );
};
